// autopilot-doctor-environment-deps.ts
//
// Req 3 (phase 05, D9): builds the REAL `RunEnvironmentChecksDeps` for
// `autopilot doctor` — split out of autopilot-doctor-command.ts to keep
// that file's action body readable. Works with OR without a resolved
// mission: `mission` ({workspace, controlRoot}) is optional because a
// workspace with 0 missions yet must still run the environment group
// (D9 — the bug this phase fixes: `resolveMission` used to throw before
// doctor could check anything).

import { join } from "node:path";
import type {
  MattermostBannerState,
  RunEnvironmentChecksDeps,
} from "../lib/autopilot/autopilot-environment-checks.js";
import { probeBinaryVersion, probeClaudeVersion } from "../lib/autopilot/claude-version-probe.js";
import { findUnusedKeysDeclaredInFile } from "../lib/autopilot/config-keys-not-read-in-v1.js";
import { permissionsFallbackProblem } from "../lib/autopilot/effective-config-loader.js";
import { lookupGitlabPat } from "../lib/autopilot/gitlab-pat-lookup.js";
import { readMattermostCredentials } from "../lib/autopilot/mattermost-credentials-file.js";
import {
  evaluateMattermostConfigPreconditions,
  evaluateMattermostMissionPreconditions,
} from "../lib/autopilot/mattermost-transport-preconditions.js";
import { readMissionActorBinding } from "../lib/autopilot/mission-actor-binding.js";
import { PRINCIPLES_RELATIVE_PATH } from "../lib/autopilot/run-command-prompt-builder.js";
import type { AutopilotConfig } from "../lib/autopilot/schemas/config-schema.js";
import { AutopilotConfigSchema } from "../lib/autopilot/schemas/config-schema.js";
import { realGitRunner } from "../lib/autopilot/supervisor-git-runner.js";
import { checkClaudeCodeSubscriptionAuth } from "../lib/check-claude-code-subscription-and-quota.js";
import { pathExists } from "../lib/filesystem-helpers.js";
import { parseGitRemoteHost } from "../lib/git-remote-host.js";

/** Read-only parse (no durable snapshot) — used only by the no-mission doctor path, which has no `controlRoot` to write one into. D7: `unusedKeyWarnings` mirrors loadEffectiveConfig's own field, `[]` for the ENOENT seed branch (same "seed is never a user declaration" rule). */
export async function parseGlobalConfigReadOnly(
  globalConfigPath: string,
): Promise<
  | { ok: true; config: AutopilotConfig; unusedKeyWarnings: string[] }
  | { ok: false; message: string }
> {
  let text: string;
  try {
    const { readFile } = await import("node:fs/promises");
    text = await readFile(globalConfigPath, "utf8");
  } catch (err) {
    if ((err as NodeJS.ErrnoException)?.code === "ENOENT") {
      const seed = { mattermost: { channel_name: "autopilot", approvers: [] } };
      const parsed = AutopilotConfigSchema.parse(seed);
      return { ok: true, config: parsed, unusedKeyWarnings: [] };
    }
    return { ok: false, message: `không đọc được global config: ${String(err)}` };
  }
  // H19: a syntax error is `config_invalid` like every other bad config, not a throw.
  let rawJson: unknown;
  try {
    rawJson = JSON.parse(text);
  } catch (err) {
    return {
      ok: false,
      message: `global config không phải JSON hợp lệ (${globalConfigPath}): ${err instanceof Error ? err.message : String(err)}`,
    };
  }
  const parsed = AutopilotConfigSchema.safeParse(rawJson);
  if (!parsed.success) {
    return { ok: false, message: parsed.error.message };
  }
  const fallbackProblem = permissionsFallbackProblem(parsed.data);
  if (fallbackProblem !== undefined) return { ok: false, message: fallbackProblem };
  return {
    ok: true,
    config: parsed.data,
    unusedKeyWarnings: findUnusedKeysDeclaredInFile(rawJson),
  };
}

/**
 * What `run` would say about Mattermost, from LOCAL facts only (X6: the shared
 * verdict of mattermost-transport-preconditions.ts — one order, no drift).
 * With a `mission` (one was resolved) the mission's actor binding is judged
 * too, so an approver the mission does not name shows up here as a `warn`
 * instead of "bật" (L5); without one there is no binding to read.
 */
async function probeMattermostState(
  workspaceRoot: string,
  config: AutopilotConfig,
  mission: DoctorMissionInput | undefined,
): Promise<MattermostBannerState> {
  const credentials = await readMattermostCredentials(workspaceRoot);
  const verdict =
    mission === undefined
      ? evaluateMattermostConfigPreconditions({ config, credentials })
      : evaluateMattermostMissionPreconditions({
          config,
          credentials,
          binding: await readMissionActorBinding(mission.controlRoot),
        });
  return verdict.enabled
    ? { enabled: true, channelId: verdict.channelId }
    : { enabled: false, reason: verdict.reason };
}

/** GitLab PAT check — only meaningful when `config.release` is set (Req 3); `repoDir` is the execution repo to read `git remote get-url` from. */
async function checkGitlabPatForRelease(
  repoDir: string,
  config: AutopilotConfig,
): Promise<{ found: boolean }> {
  if (!config.release) return { found: true }; // no release configured => nothing to warn about
  const remoteUrl = await realGitRunner(
    ["remote", "get-url", config.release.remote],
    repoDir,
  ).catch(() => undefined);
  const host = remoteUrl ? parseGitRemoteHost(remoteUrl.stdout.trim()) : null;
  if (!host) return { found: false };
  return lookupGitlabPat(host);
}

/** M-1 (review 261001): the two paths a resolved mission contributes travel as ONE value — a caller cannot pass the workspace and forget the control root (which would silently skip the binding half of the Mattermost check and print "bật" for a mission `run` refuses). */
export interface DoctorMissionInput {
  /** The mission's execution repo — enables the GitLab PAT check and drops the duplicate python3 probe. */
  workspace: string;
  /** Enables the actor-binding half of the Mattermost check (L5: approver not bound to the mission). */
  controlRoot: string;
}

export interface BuildDoctorEnvironmentDepsInput {
  workspaceRoot: string;
  config: AutopilotConfig;
  /** Present only when a mission was resolved (see DoctorMissionInput). */
  mission?: DoctorMissionInput;
  /** D7 (R-18): forwarded verbatim into RunEnvironmentChecksDeps — [] or undefined => no `config-unused-keys` check (Req 3). */
  unusedConfigKeyWarnings?: string[];
}

export async function buildDoctorEnvironmentDeps(
  input: BuildDoctorEnvironmentDepsInput,
): Promise<RunEnvironmentChecksDeps> {
  const { mission } = input;
  return {
    probeClaudeVersion,
    probeBinaryVersion,
    // With a resolved mission the mission-level list (runAutopilotDoctorChecks)
    // already reports "Hook interpreter (python3)" — probe only `git` here so
    // `doctor --mission` prints one python3 line, not two. No mission => both.
    ...(mission ? { binariesToProbe: ["git"] as const } : {}),
    checkClaudeCodeSubscriptionAuth,
    packHasAutopilotWorkflows: async () => {
      const packRoot = join(input.workspaceRoot, ".claude", "pack");
      return pathExists(join(packRoot, PRINCIPLES_RELATIVE_PATH));
    },
    effectiveConfig: { ok: true },
    unusedConfigKeyWarnings: input.unusedConfigKeyWarnings,
    mattermost: await probeMattermostState(input.workspaceRoot, input.config, mission),
    env: process.env,
    ...(mission
      ? { checkGitlabPat: () => checkGitlabPatForRelease(mission.workspace, input.config) }
      : {}),
  };
}
